import { createContext, useContext, useState, useEffect } from "react";

const translations = {
  it: {
    nav: { servizi: "Servizi", flotta: "Flotta", tariffe: "Tariffe", prenota: "Prenota", contatti: "Contatti", call: "Chiama" },
    hero: {
      badge: "24/7 attivo ora • Bellinzona & Ticino",
      line1: "Taxi",
      line2: "Bellinzona",
      line3: "Il tuo viaggio 24/7 nel cuore del Ticino",
      sub: "Servizio taxi rapido, Minivan 8 posti, trasferimenti aeroporto e corriere aziendale. Oltre 22 anni di puntualità e riservatezza svizzera.",
      h24: "24h/7",
      ctaSecondary: "Aziendale & Minivan",
      note1: "Rispondiamo 24h su 24",
      note2: "7 giorni su 7",
    },
    marquee: ["Taxi Bellinzona", "Disponibile 24/7", "Trasporti Aeroporto", "Minivan 8 Posti", "Servizio Aziendale", "Corriere Espresso", "Puntualità Ticino"],
    services: {
      eyebrow: "I Nostri Servizi",
      title: "Ogni viaggio,",
      accent: "la stessa precisione.",
      items: [
        { title: "Auto Comode in Città", badge: "24/7 Urbano", desc: "Servizio taxi per accompagnarVi in qualsiasi luogo della città o oltre, senza pensieri." },
        { title: "Minivan & Navetta 8 Posti", badge: "Gruppi & Eventi", desc: "Spostamenti sul territorio col nostro monovolume a capienza 8 posti: sostenibile ed economico." },
        { title: "Auto Elettrica & Eco Viaggi", badge: "100% Eco", desc: "Viaggi ecologici su medie e lunghe tratte con i nostri mezzi totalmente eco-free." },
        { title: "Trasferimenti Aeroporto", badge: "Voli Garantiti", desc: "Malpensa, Linate, Zurigo, Bergamo e Lugano Agno. Sappiamo in tempo reale se il tuo volo è confermato." },
        { title: "Servizio Taxi & Corriere Aziendale", badge: "Business & VIP", desc: "Lusso e comodità su richiesta anticipata: trasferte sicure, consegna urgente documenti e colli." },
      ],
    },
    fleet: {
      eyebrow: "La Flotta",
      title: "Flotta dotata di",
      accent: "defibrillatori DAE.",
      cta: "Prenota questa vettura",
      items: [
        { id: "berlina", label: "Berlina Premium", desc: "Eleganza e riservatezza per viaggi di lavoro e serate speciali. Comfort assoluto in ogni dettaglio.", points: ["Fino a 4 passeggeri", "Climatizzazione personalizzata", "Wi-Fi e acqua di cortesia"] },
        { id: "minivan", label: "Minivan 8 Posti", desc: "La scelta ideale per gruppi, famiglie ed eventi. Riserva in tempo: autista disponibile per tutta la notte.", points: ["8 posti + bagagli", "Perfetto per eventi e cerimonie", "Vai a vedere la tua squadra del cuore"] },
        { id: "elettrica", label: "Auto Elettrica", desc: "Trasporti in città in modo ecologico: zero emissioni, silenzio totale, stessa classe di sempre.", points: ["Zero emissioni", "Silenziosità premium", "Viaggi eco-free su lunghe tratte"] },
      ],
    },
    stats: {
      items: [
        { label: "Anni di attività in Ticino" },
        { label: "Viaggi aeroporto senza mai perdere una coincidenza" },
        { label: "Reperibilità immediata tutto l'anno" },
        { label: "Puntualità e privacy garantita" },
      ],
      quote: "In 22 anni di attività abbiamo completato 6'790 viaggi verso gli aeroporti della regione senza mai perdere una coincidenza. Sappiamo in tempo reale se il tuo volo è confermato o in ritardo.",
      caption: "Taxi Bellinzona • Dal 2003",
      watermark: "22 Anni",
    },
    reviews: {
      eyebrow: "Dicono di Noi",
      title: "Clienti che tornano,",
      accent: "viaggio dopo viaggio.",
      disclaimer: "Recensioni raccolte dai nostri passeggeri abituali",
      items: [
        { name: "Marco P.", place: "Lugano", tag: "Transfer Aeroporto", text: "Prenotato alle 4 del mattino per Malpensa: autista puntuale sotto casa alle 4:30 precise. Impeccabile." },
        { name: "Famiglia Bernasconi", place: "Bellinzona", tag: "Minivan 8 Posti", text: "Minivan perfetto per 8 persone con bagagli. Autista gentilissimo e prezzo onesto." },
        { name: "Anna K.", place: "Zurigo", tag: "Servizio Aziendale", text: "Servizio aziendale riservato e professionale. Fatturazione mensile comodissima." },
        { name: "Davide R.", place: "Locarno", tag: "Servizio Notturno", text: "Tornato a casa di notte dopo il concerto: una chiamata e in 10 minuti il taxi era lì." },
      ],
    },
    whyus: {
      eyebrow: "Perché Scegliere Noi",
      title: "Le persone sono entusiaste.",
      accent: "E si sente.",
      items: [
        { title: "Autisti Gentili e Riservati", desc: "Le persone parlano bene dei nostri servizi perché li facciamo sentire a loro agio per tutto il viaggio." },
        { title: "Attenzione ai Particolari", desc: "Prestiamo attenzione: curiamo i particolari e questo fa la differenza. Il cliente ha fiducia in noi." },
        { title: "Flessibilità di Pagamento", desc: "Carte di credito, TWINT, Apple Pay, Google Pay e contanti: paghi come preferisci." },
        { title: "Copertura Totale Ticino", desc: "Da Bellinzona verso Lugano, Locarno, Ascona, Chiasso, il San Gottardo e destinazioni estere." },
      ],
    },
    fare: {
      eyebrow: "Stima Immediata",
      title: "Quanto costa",
      accent: "la tua corsa?",
      originLabel: "Partenza",
      destLabel: "Destinazione",
      estimate: "Tariffa stimata",
      duration: "durata stimata",
      callCta: "Chiama e prenota",
      bookCta: "Prenota online",
      note: "Per trasferte aeroportuali è gradita la riservazione.",
      origins: ["Bellinzona Centro", "Stazione FFS Bellinzona", "Giubiasco", "Castelgrande"],
      destinations: [
        { name: "Corsa in città", price: "CHF 15 – 25", time: "5 – 15 min" },
        { name: "Lugano", price: "CHF 65 – 80", time: "≈ 25 min" },
        { name: "Locarno / Ascona", price: "CHF 55 – 70", time: "≈ 25 min" },
        { name: "Chiasso / Confine", price: "CHF 85 – 100", time: "≈ 40 min" },
        { name: "Aeroporto Lugano-Agno", price: "CHF 75 – 90", time: "≈ 30 min" },
        { name: "Aeroporto Milano Malpensa", price: "CHF 180 – 210", time: "≈ 1 h 10" },
        { name: "Aeroporto Milano Linate", price: "CHF 200 – 230", time: "≈ 1 h 20" },
        { name: "Aeroporto Bergamo Orio al Serio", price: "CHF 220 – 260", time: "≈ 1 h 40" },
        { name: "Aeroporto Zurigo", price: "CHF 300 – 350", time: "≈ 2 h" },
        { name: "St. Moritz / Engadina", price: "CHF 260 – 320", time: "≈ 2 h 15" },
      ],
    },
    booking: {
      eyebrow: "Prenotazione Online",
      title: "Prenota la corsa,",
      accent: "senza chiamare.",
      watermark: "Prenota",
      date: "Data",
      time: "Ora",
      origin: "Partenza",
      passengers: "Passeggeri",
      destination: "Destinazione",
      destPlaceholder: "Es. Aeroporto Milano Malpensa, Lugano, Locarno...",
      name: "Nome e Cognome",
      phone: "Telefono",
      email: "Email",
      notes: "Note (bagagli, seggiolino, volo...)",
      notesPlaceholder: "Es. volo LX 1234, 2 valigie grandi...",
      submit: "Conferma Prenotazione",
      sending: "Invio in corso...",
      success: "Prenotazione inviata! Ti confermeremo la corsa a breve.",
      error: "Invio non riuscito. Chiamaci al +41 76 634 4242.",
      immediate: "Per partenze immediate chiama la centrale: +41 76 634 4242",
      origins: ["Bellinzona Centro", "Stazione FFS Bellinzona", "Giubiasco", "Castelgrande", "Altro (indica nelle note)"],
    },
    contact: {
      eyebrow: "Richiedi un Preventivo",
      title: "Prenota la tua corsa",
      accent: "in un minuto.",
      intro: "Compila il modulo per preventivi, riservazioni aeroporto o noleggio minivan con autista. Per partenze immediate chiama direttamente la centrale: rispondiamo sempre.",
      name: "Nome e Cognome",
      phone: "Telefono",
      email: "Email",
      subject: "Oggetto",
      subjectPlaceholder: "Transfer aeroporto",
      message: "Messaggio / Dettagli corsa",
      messagePlaceholder: "Data, ora, numero passeggeri, destinazione...",
      submit: "Invia Richiesta",
      sending: "Invio in corso...",
      success: "Richiesta inviata! Ti ricontattiamo al più presto.",
      error: "Invio non riuscito. Chiamaci al +41 76 634 4242.",
    },
    footer: {
      tagline: "Flotta dotata di defibrillatori DAE. Servizio taxi 24h su 24, 7 giorni su 7. Bellinzona, Svizzera.",
      contacts: "Contatti",
      central: "Centrale 24/7",
      business: "Aziendale & Minivan",
      follow: "Seguici",
      media: "Media Partner",
      rights: "Taxi Bellinzona — Bellinzona, Ticino, Svizzera",
      service24: "Servizio Taxi 24 H",
    },
    floating: {
      call: "Chiama ora",
      whatsapp: "WhatsApp",
      waText: "Buongiorno, vorrei prenotare un taxi.",
    },
  },
  de: {
    nav: { servizi: "Dienste", flotta: "Flotte", tariffe: "Tarife", prenota: "Buchen", contatti: "Kontakt", call: "Anrufen" },
    hero: {
      badge: "24/7 jetzt aktiv • Bellinzona & Tessin",
      line1: "Taxi",
      line2: "Bellinzona",
      line3: "Ihre Fahrt 24/7 im Herzen des Tessins",
      sub: "Schneller Taxiservice, 8-Sitzer-Minivan, Flughafentransfers und Firmenkurier. Über 22 Jahre Pünktlichkeit und Schweizer Diskretion.",
      h24: "24h/7",
      ctaSecondary: "Firmen & Minivan",
      note1: "Wir antworten rund um die Uhr",
      note2: "7 Tage die Woche",
    },
    marquee: ["Taxi Bellinzona", "Verfügbar 24/7", "Flughafentransfers", "Minivan 8 Plätze", "Firmenservice", "Express-Kurier", "Pünktlichkeit Tessin"],
    services: {
      eyebrow: "Unsere Dienste",
      title: "Jede Fahrt,",
      accent: "dieselbe Präzision.",
      items: [
        { title: "Komfortabel in der Stadt", badge: "24/7 Stadt", desc: "Taxiservice, der Sie überall in der Stadt und darüber hinaus hinfährt — ganz ohne Sorgen." },
        { title: "Minivan & Shuttle 8 Plätze", badge: "Gruppen & Events", desc: "Fahrten im ganzen Gebiet mit unserem 8-Sitzer-Van: nachhaltig und günstig." },
        { title: "Elektroauto & Eco-Fahrten", badge: "100% Eco", desc: "Ökologische Fahrten auf mittleren und langen Strecken mit unseren umweltfreundlichen Fahrzeugen." },
        { title: "Flughafentransfers", badge: "Garantierte Flüge", desc: "Malpensa, Linate, Zürich, Bergamo und Lugano Agno. Wir wissen in Echtzeit, ob Ihr Flug bestätigt ist." },
        { title: "Firmen-Taxi & Kurierdienst", badge: "Business & VIP", desc: "Luxus und Komfort auf Vorbestellung: sichere Geschäftsreisen, dringende Zustellung von Dokumenten und Paketen." },
      ],
    },
    fleet: {
      eyebrow: "Die Flotte",
      title: "Flotte ausgestattet mit",
      accent: "DAE-Defibrillatoren.",
      cta: "Dieses Fahrzeug buchen",
      items: [
        { id: "berlina", label: "Premium-Limousine", desc: "Eleganz und Diskretion für Geschäftsreisen und besondere Abende. Absoluter Komfort in jedem Detail.", points: ["Bis zu 4 Passagiere", "Individuelle Klimatisierung", "WLAN und Wasser an Bord"] },
        { id: "minivan", label: "Minivan 8 Plätze", desc: "Die ideale Wahl für Gruppen, Familien und Events. Rechtzeitig buchen: Fahrer die ganze Nacht verfügbar.", points: ["8 Plätze + Gepäck", "Perfekt für Events und Feiern", "Fahren Sie Ihr Lieblingsteam ansehen"] },
        { id: "elettrica", label: "Elektroauto", desc: "Ökologisch durch die Stadt: null Emissionen, absolute Stille, dieselbe Klasse wie immer.", points: ["Null Emissionen", "Premium-Geräuschlosigkeit", "Eco-freie Fahrten auf langen Strecken"] },
      ],
    },
    stats: {
      items: [
        { label: "Jahre Erfahrung im Tessin" },
        { label: "Flughafenfahrten ohne je einen Anschluss zu verpassen" },
        { label: "Sofortige Erreichbarkeit das ganze Jahr" },
        { label: "Garantierte Pünktlichkeit und Privatsphäre" },
      ],
      quote: "In 22 Jahren haben wir 6'790 Fahrten zu den Flughäfen der Region durchgeführt, ohne je einen Anschluss zu verpassen. Wir wissen in Echtzeit, ob Ihr Flug bestätigt ist oder Verspätung hat.",
      caption: "Taxi Bellinzona • Seit 2003",
      watermark: "22 Jahre",
    },
    reviews: {
      eyebrow: "Das sagen unsere Kunden",
      title: "Kunden, die wiederkommen,",
      accent: "Fahrt für Fahrt.",
      disclaimer: "Bewertungen unserer Stammkunden",
      items: [
        { name: "Marco P.", place: "Lugano", tag: "Flughafentransfer", text: "Um 4 Uhr morgens für Malpensa gebucht: der Fahrer stand um 4:30 Uhr pünktlich vor der Tür. Makellos." },
        { name: "Familie Bernasconi", place: "Bellinzona", tag: "Minivan 8 Plätze", text: "Perfekter Minivan für 8 Personen mit Gepäck. Sehr freundlicher Fahrer und fairer Preis." },
        { name: "Anna K.", place: "Zürich", tag: "Firmenservice", text: "Diskreter und professioneller Firmenservice. Monatliche Abrechnung sehr praktisch." },
        { name: "Davide R.", place: "Locarno", tag: "Nachtservice", text: "Nach dem Konzert nachts nach Hause: ein Anruf und in 10 Minuten war das Taxi da." },
      ],
    },
    whyus: {
      eyebrow: "Warum wir",
      title: "Die Leute sind begeistert.",
      accent: "Und man merkt es.",
      items: [
        { title: "Freundliche und diskrete Fahrer", desc: "Man spricht gut über uns, weil sich unsere Gäste die ganze Fahrt über wohlfühlen." },
        { title: "Liebe zum Detail", desc: "Wir achten auf jedes Detail — das macht den Unterschied. Unsere Kunden vertrauen uns." },
        { title: "Flexible Zahlung", desc: "Kreditkarten, TWINT, Apple Pay, Google Pay und Bargeld: zahlen Sie, wie Sie möchten." },
        { title: "Ganzes Tessin abgedeckt", desc: "Von Bellinzona nach Lugano, Locarno, Ascona, Chiasso, über den Gotthard und ins Ausland." },
      ],
    },
    fare: {
      eyebrow: "Sofortige Schätzung",
      title: "Was kostet",
      accent: "Ihre Fahrt?",
      originLabel: "Abfahrt",
      destLabel: "Ziel",
      estimate: "Geschätzter Preis",
      duration: "geschätzte Dauer",
      callCta: "Anrufen & buchen",
      bookCta: "Online buchen",
      note: "Für Flughafentransfers wird eine Reservation empfohlen.",
      origins: ["Bellinzona Zentrum", "Bahnhof Bellinzona", "Giubiasco", "Castelgrande"],
      destinations: [
        { name: "Stadtfahrt", price: "CHF 15 – 25", time: "5 – 15 Min" },
        { name: "Lugano", price: "CHF 65 – 80", time: "≈ 25 Min" },
        { name: "Locarno / Ascona", price: "CHF 55 – 70", time: "≈ 25 Min" },
        { name: "Chiasso / Grenze", price: "CHF 85 – 100", time: "≈ 40 Min" },
        { name: "Flughafen Lugano-Agno", price: "CHF 75 – 90", time: "≈ 30 Min" },
        { name: "Flughafen Mailand Malpensa", price: "CHF 180 – 210", time: "≈ 1 Std 10" },
        { name: "Flughafen Mailand Linate", price: "CHF 200 – 230", time: "≈ 1 Std 20" },
        { name: "Flughafen Bergamo Orio al Serio", price: "CHF 220 – 260", time: "≈ 1 Std 40" },
        { name: "Flughafen Zürich", price: "CHF 300 – 350", time: "≈ 2 Std" },
        { name: "St. Moritz / Engadin", price: "CHF 260 – 320", time: "≈ 2 Std 15" },
      ],
    },
    booking: {
      eyebrow: "Online-Buchung",
      title: "Buchen Sie Ihre Fahrt,",
      accent: "ohne Anruf.",
      watermark: "Buchen",
      date: "Datum",
      time: "Uhrzeit",
      origin: "Abfahrt",
      passengers: "Passagiere",
      destination: "Ziel",
      destPlaceholder: "Z.B. Flughafen Mailand Malpensa, Lugano, Locarno...",
      name: "Vor- und Nachname",
      phone: "Telefon",
      email: "E-Mail",
      notes: "Notizen (Gepäck, Kindersitz, Flug...)",
      notesPlaceholder: "Z.B. Flug LX 1234, 2 grosse Koffer...",
      submit: "Buchung bestätigen",
      sending: "Wird gesendet...",
      success: "Buchung gesendet! Wir bestätigen Ihre Fahrt in Kürze.",
      error: "Senden fehlgeschlagen. Rufen Sie uns an: +41 76 634 4242.",
      immediate: "Für sofortige Abfahrten rufen Sie die Zentrale an: +41 76 634 4242",
      origins: ["Bellinzona Zentrum", "Bahnhof Bellinzona", "Giubiasco", "Castelgrande", "Andere (in den Notizen angeben)"],
    },
    contact: {
      eyebrow: "Offerte anfordern",
      title: "Ihre Fahrt buchen",
      accent: "in einer Minute.",
      intro: "Füllen Sie das Formular für Offerten, Flughafenreservationen oder Minivan-Miete mit Fahrer aus. Für sofortige Abfahrten rufen Sie direkt die Zentrale an: wir antworten immer.",
      name: "Vor- und Nachname",
      phone: "Telefon",
      email: "E-Mail",
      subject: "Betreff",
      subjectPlaceholder: "Flughafentransfer",
      message: "Nachricht / Fahrtdetails",
      messagePlaceholder: "Datum, Uhrzeit, Anzahl Passagiere, Ziel...",
      submit: "Anfrage senden",
      sending: "Wird gesendet...",
      success: "Anfrage gesendet! Wir melden uns so schnell wie möglich.",
      error: "Senden fehlgeschlagen. Rufen Sie uns an: +41 76 634 4242.",
    },
    footer: {
      tagline: "Flotte mit DAE-Defibrillatoren ausgestattet. Taxiservice 24 Stunden, 7 Tage die Woche. Bellinzona, Schweiz.",
      contacts: "Kontakt",
      central: "Zentrale 24/7",
      business: "Firmen & Minivan",
      follow: "Folgen Sie uns",
      media: "Medienpartner",
      rights: "Taxi Bellinzona — Bellinzona, Tessin, Schweiz",
      service24: "Taxiservice 24 Std",
    },
    floating: {
      call: "Anrufen",
      whatsapp: "WhatsApp",
      waText: "Guten Tag, ich möchte ein Taxi buchen.",
    },
  },
  en: {
    nav: { servizi: "Services", flotta: "Fleet", tariffe: "Fares", prenota: "Book", contatti: "Contact", call: "Call" },
    hero: {
      badge: "24/7 active now • Bellinzona & Ticino",
      line1: "Taxi",
      line2: "Bellinzona",
      line3: "Your ride 24/7 in the heart of Ticino",
      sub: "Fast taxi service, 8-seat minivan, airport transfers and corporate courier. Over 22 years of Swiss punctuality and discretion.",
      h24: "24h/7",
      ctaSecondary: "Corporate & Minivan",
      note1: "We answer 24 hours a day",
      note2: "7 days a week",
    },
    marquee: ["Taxi Bellinzona", "Available 24/7", "Airport Transfers", "8-Seat Minivan", "Corporate Service", "Express Courier", "Ticino Punctuality"],
    services: {
      eyebrow: "Our Services",
      title: "Every ride,",
      accent: "the same precision.",
      items: [
        { title: "Comfortable City Cars", badge: "24/7 Urban", desc: "Taxi service taking you anywhere in the city and beyond — no worries." },
        { title: "Minivan & 8-Seat Shuttle", badge: "Groups & Events", desc: "Trips across the region in our 8-seat van: sustainable and affordable." },
        { title: "Electric Car & Eco Rides", badge: "100% Eco", desc: "Eco-friendly journeys on medium and long distances with our fully green vehicles." },
        { title: "Airport Transfers", badge: "Guaranteed Flights", desc: "Malpensa, Linate, Zurich, Bergamo and Lugano Agno. We know in real time if your flight is confirmed." },
        { title: "Corporate Taxi & Courier", badge: "Business & VIP", desc: "Luxury and comfort on advance request: safe business trips, urgent document and parcel delivery." },
      ],
    },
    fleet: {
      eyebrow: "The Fleet",
      title: "Fleet equipped with",
      accent: "AED defibrillators.",
      cta: "Book this vehicle",
      items: [
        { id: "berlina", label: "Premium Sedan", desc: "Elegance and discretion for business trips and special evenings. Absolute comfort in every detail.", points: ["Up to 4 passengers", "Personal climate control", "Wi-Fi and complimentary water"] },
        { id: "minivan", label: "8-Seat Minivan", desc: "The ideal choice for groups, families and events. Book ahead: driver available all night long.", points: ["8 seats + luggage", "Perfect for events and ceremonies", "Ride to watch your favourite team"] },
        { id: "elettrica", label: "Electric Car", desc: "Eco-friendly city transport: zero emissions, total silence, the same class as always.", points: ["Zero emissions", "Premium quietness", "Eco-free long-distance rides"] },
      ],
    },
    stats: {
      items: [
        { label: "Years of activity in Ticino" },
        { label: "Airport rides without ever missing a connection" },
        { label: "Immediate availability all year round" },
        { label: "Guaranteed punctuality and privacy" },
      ],
      quote: "In 22 years we have completed 6,790 rides to the region's airports without ever missing a connection. We know in real time whether your flight is confirmed or delayed.",
      caption: "Taxi Bellinzona • Since 2003",
      watermark: "22 Years",
    },
    reviews: {
      eyebrow: "What Clients Say",
      title: "Clients who come back,",
      accent: "ride after ride.",
      disclaimer: "Reviews from our regular passengers",
      items: [
        { name: "Marco P.", place: "Lugano", tag: "Airport Transfer", text: "Booked at 4 am for Malpensa: the driver was outside my door at exactly 4:30. Impeccable." },
        { name: "Bernasconi Family", place: "Bellinzona", tag: "8-Seat Minivan", text: "Perfect minivan for 8 people with luggage. Extremely kind driver and fair price." },
        { name: "Anna K.", place: "Zurich", tag: "Corporate Service", text: "Discreet, professional corporate service. Monthly billing is very convenient." },
        { name: "Davide R.", place: "Locarno", tag: "Night Service", text: "Got home at night after the concert: one call and the taxi arrived in 10 minutes." },
      ],
    },
    whyus: {
      eyebrow: "Why Choose Us",
      title: "People are enthusiastic.",
      accent: "And it shows.",
      items: [
        { title: "Kind and Discreet Drivers", desc: "People speak well of our services because we make them feel at ease for the whole ride." },
        { title: "Attention to Detail", desc: "We care about the details — that makes the difference. Our clients trust us." },
        { title: "Flexible Payment", desc: "Credit cards, TWINT, Apple Pay, Google Pay and cash: pay the way you prefer." },
        { title: "Full Ticino Coverage", desc: "From Bellinzona to Lugano, Locarno, Ascona, Chiasso, across the Gotthard and abroad." },
      ],
    },
    fare: {
      eyebrow: "Instant Estimate",
      title: "How much is",
      accent: "your ride?",
      originLabel: "Pick-up",
      destLabel: "Destination",
      estimate: "Estimated fare",
      duration: "estimated duration",
      callCta: "Call & book",
      bookCta: "Book online",
      note: "Advance booking is appreciated for airport transfers.",
      origins: ["Bellinzona Centre", "Bellinzona Station", "Giubiasco", "Castelgrande"],
      destinations: [
        { name: "City ride", price: "CHF 15 – 25", time: "5 – 15 min" },
        { name: "Lugano", price: "CHF 65 – 80", time: "≈ 25 min" },
        { name: "Locarno / Ascona", price: "CHF 55 – 70", time: "≈ 25 min" },
        { name: "Chiasso / Border", price: "CHF 85 – 100", time: "≈ 40 min" },
        { name: "Lugano-Agno Airport", price: "CHF 75 – 90", time: "≈ 30 min" },
        { name: "Milan Malpensa Airport", price: "CHF 180 – 210", time: "≈ 1 h 10" },
        { name: "Milan Linate Airport", price: "CHF 200 – 230", time: "≈ 1 h 20" },
        { name: "Bergamo Orio al Serio Airport", price: "CHF 220 – 260", time: "≈ 1 h 40" },
        { name: "Zurich Airport", price: "CHF 300 – 350", time: "≈ 2 h" },
        { name: "St. Moritz / Engadine", price: "CHF 260 – 320", time: "≈ 2 h 15" },
      ],
    },
    booking: {
      eyebrow: "Online Booking",
      title: "Book your ride,",
      accent: "no call needed.",
      watermark: "Book",
      date: "Date",
      time: "Time",
      origin: "Pick-up",
      passengers: "Passengers",
      destination: "Destination",
      destPlaceholder: "E.g. Milan Malpensa Airport, Lugano, Locarno...",
      name: "Full name",
      phone: "Phone",
      email: "Email",
      notes: "Notes (luggage, child seat, flight...)",
      notesPlaceholder: "E.g. flight LX 1234, 2 large suitcases...",
      submit: "Confirm Booking",
      sending: "Sending...",
      success: "Booking sent! We will confirm your ride shortly.",
      error: "Sending failed. Call us at +41 76 634 4242.",
      immediate: "For immediate pick-up call the dispatch: +41 76 634 4242",
      origins: ["Bellinzona Centre", "Bellinzona Station", "Giubiasco", "Castelgrande", "Other (specify in notes)"],
    },
    contact: {
      eyebrow: "Request a Quote",
      title: "Book your ride",
      accent: "in one minute.",
      intro: "Fill in the form for quotes, airport reservations or minivan hire with driver. For immediate pick-up call the dispatch directly: we always answer.",
      name: "Full name",
      phone: "Phone",
      email: "Email",
      subject: "Subject",
      subjectPlaceholder: "Airport transfer",
      message: "Message / Ride details",
      messagePlaceholder: "Date, time, number of passengers, destination...",
      submit: "Send Request",
      sending: "Sending...",
      success: "Request sent! We will get back to you as soon as possible.",
      error: "Sending failed. Call us at +41 76 634 4242.",
    },
    footer: {
      tagline: "Fleet equipped with AED defibrillators. Taxi service 24 hours a day, 7 days a week. Bellinzona, Switzerland.",
      contacts: "Contact",
      central: "Dispatch 24/7",
      business: "Corporate & Minivan",
      follow: "Follow us",
      media: "Media Partners",
      rights: "Taxi Bellinzona — Bellinzona, Ticino, Switzerland",
      service24: "Taxi Service 24 H",
    },
    floating: {
      call: "Call now",
      whatsapp: "WhatsApp",
      waText: "Hello, I would like to book a taxi.",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("tb-lang") || "it");

  useEffect(() => {
    localStorage.setItem("tb-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
